import React, { useRef, useEffect, useReducer, useCallback } from 'react'
import PropTypes from 'prop-types'
import MUIRichTextEditor from 'mui-rte'
import { SelectionState, convertToRaw, EditorState, Modifier } from 'draft-js'
import 'draft-js/dist/Draft.css'
import { makeStyles, useTheme } from '@material-ui/styles'
import { stateToHTML } from 'draft-js-export-html'
import { stateFromHTML } from 'draft-js-import-html'
import * as R from 'ramda'
import { Typography } from '@material-ui/core'
import { displayCountLimit } from 'commons/number'
import { FormattedMessage } from 'react-intl'
import DoNotTranslateIcon from './DoNotTranslateIcon'
import { useAppSettings, getSkipTranslation } from 'commons/useAppSettings'
import { useBreakpoints } from 'commons/useBreakpoints'
import {
  removeNewline,
  defaultTextCount,
  htmlToTextCount,
  textToTextCount,
} from 'libs/text'
import linkifyIt from 'linkify-it'
import tlds from 'tlds'
import { useWindowSize } from 'commons/useWindowSize'
import clsx from 'clsx'

const linkify = linkifyIt()
linkify.tlds(tlds)

const styleReadOnly = makeStyles(() => {
  return {
    textReadOnly: { color: 'rgba(0, 0, 0, 0.38)' },
  }
})

const useStyles = makeStyles((theme) => {
  return {
    root: {
      wordBreak: 'break-word',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      margin: '0px',
    },
    editor: {
      paddingLeft: '14px',
      paddingRight: '14px',
      '& .public-DraftStyleDefault-block': {
        margin: '0',
      },
      display: 'flex',
      alignItems: 'center',
    },
    editorContainer: {
      margin: '0px',
      padding: '12px 0 12px 0',
    },
    toolbar: {
      borderStyle: 'solid',
      borderBottomWidth: '1px',
      borderColor: 'rgba(0, 0, 0, 0.23)',
      display: 'flex',
      flexWrap: 'wrap',
      '& > *': {
        padding: '12px',
      },
      [useBreakpoints(theme).lessThan.md]: {
        '& > *': {
          padding: '8px',
        },
      },
      [useBreakpoints(theme).lessThan.sm]: {
        '& > *': {
          padding: '5px',
        },
      },
    },
    placeHolder: {
      paddingLeft: '14px',
      paddingTop: '12px',
      paddingBottom: '12px',
      position: 'relative',
    },
  }
})

const useStylesInline = makeStyles((theme) => {
  return {
    root: {
      wordBreak: 'break-word',
    },
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      flexDirection: 'row-reverse',
      margin: '0px',
      minHeight: '48px',
    },
    editor: {
      paddingLeft: '12px',
      flexGrow: '1',
      minWidth: '0px',
      width: '100%',
      '& focus': {
        width: '93%',
      },
      '& .public-DraftStyleDefault-block': {
        margin: '0',
      },
      display: 'flex',
      alignItems: 'center',
    },
    editorContainer: {
      margin: '0px',
      padding: '12px 0 12px 0',
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginRight: '12px',
      alignItems: 'center',
      width: '125px',
      '& .MuiButtonBase-root': {
        padding: '12px 0 !important',
      },
    },
    placeHolder: {
      paddingLeft: '14px',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
  }
})

const useStylesBorder = makeStyles(() => {
  return {
    border: {
      borderStyle: 'solid',
      borderWidth: '1px',
      borderRadius: '4px',
    },
    borderRed: {
      borderColor: '#E3342F',
    },
    borderGrey: {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
  }
})

const NOTRANSLATE_CLASS = 'notranslate'
const SPAN_TAG = 'span'
const DONOTTRANSLATE_TYPE = 'DONOTTRANSLATE'

const isSpanTag = R.compose(R.equals(SPAN_TAG), R.toLower, R.prop('tagName'))

const hasNoTranslateClass = (element) =>
  R.equals(NOTRANSLATE_CLASS, element.getAttribute('class'))

const isNoTranslateType = R.allPass([isSpanTag, hasNoTranslateClass])

const controls = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'link',
  'numberList',
  'bulletList',
  'clear',
  DONOTTRANSLATE_TYPE,
  'textCount',
]

const stateFromHTMLWithOptions = (html) =>
  stateFromHTML(html, {
    customInlineFn(element, { Style }) {
      if (isNoTranslateType(element)) return Style(DONOTTRANSLATE_TYPE)
    },
  })

const htmlToContentState = R.unless(R.isNil, stateFromHTMLWithOptions)

const contentStateToRawState = R.unless(
  R.isNil,
  R.compose(JSON.stringify, convertToRaw),
)

// stateContentToTextCount :: StateContent -> Int
const stateContentToTextCount = defaultTextCount(
  R.compose(textToTextCount, (stateContent) => stateContent.getPlainText()),
)

const SET_INITIAL_VALUE = 'SET_INITIAL_VALUE'
const UPDATE_TEXT_COUNT = 'UPDATE_TEXT_COUNT'
const SET_MAX_LENGTH = 'SET_MAX_LENGTH'

const reducer = (state, action) => {
  switch (action.type) {
    case SET_INITIAL_VALUE: {
      const contentState = htmlToContentState(action.value)
      return {
        ...state,
        value: contentStateToRawState(contentState),
        textCount: stateContentToTextCount(contentState),
      }
    }
    case UPDATE_TEXT_COUNT:
      return R.mergeRight(state, { textCount: action.textCount })
    case SET_MAX_LENGTH:
      return R.mergeRight(state, { maxLength: action.maxLength })
    default:
      return state
  }
}

// stateToHTMLWithOptions :: (StateContent, Config) -> HTML String
const stateToHTMLWithOptions = (stateContent, config) =>
  stateToHTML(stateContent, {
    defaultBlockTag: config.includeDefaultBlockTag ? 'p' : null,
    blockStyleFn: (block) => {
      return {
        style: {
          wordBreak: 'break-word',
        },
      }
    },
    inlineStyles: {
      DONOTTRANSLATE: {
        element: SPAN_TAG,
        attributes: { class: NOTRANSLATE_CLASS, translate: 'no' },
      },
    },
  })

const linkRegex = /<a href="(.+?)">(.*?)<\/a>/

const fixBadLink = R.compose(
  R.ifElse(R.isEmpty, R.always(undefined), R.path([0, 'url'])),
  (t) => linkify.match(t),
)
const replaceBadLinkTag = R.compose(
  R.ifElse(
    R.propSatisfies(R.isNil, 0),
    R.prop(1),
    ([href, content]) => `<a href="${href}">${content}</a>`,
  ),
  R.over(R.lensPath([0]), fixBadLink),
  (match) => [R.prop(1, match), R.prop(2, match)],
  R.match(linkRegex),
)

const stateContentToHTML = R.compose(
  ([original, modified]) => [modified, !R.equals(original, modified)],
  (html) => [
    html,
    R.replace(new RegExp(linkRegex, 'g'), replaceBadLinkTag, html),
  ],
  removeNewline,
  stateToHTMLWithOptions,
)

//
// -- Auto detect link
//

function detectLink(editorState) {
  let sc
  editorState
    .getCurrentContent()
    .getBlockMap()
    .forEach((block) => {
      const links = linkify.match(block.getText()) || []
      links.forEach((link) => {
        const hasNoEntityKeys = R.compose(
          R.isEmpty,
          R.filter(R.complement(R.isNil)),
          () =>
            R.range(link.index, link.lastIndex).map((i) =>
              block.getEntityAt(i),
            ),
        )()
        if (hasNoEntityKeys) {
          sc = editorState
            .getCurrentContent()
            .createEntity('LINK', 'MUTABLE', { href: link.url })
          const entityKey = sc.getLastCreatedEntityKey()
          const contentStateWithEntity = Modifier.applyEntity(
            sc,
            new SelectionState({
              anchorKey: block.getKey(),
              anchorOffset: link.index,
              focusKey: block.getKey(),
              focusOffset: link.lastIndex,
            }),
            entityKey,
          )
          editorState = EditorState.push(
            editorState,
            contentStateWithEntity,
            'apply-entity',
          )
        }
      })
    })
  return editorState
}
// -- Control focus state --
//
const enableFocus = (element) => {
  if (element) element.tabIndex = 0
}
const disableFocus = (element) => {
  if (element) element.tabIndex = -1
}

const onContainerFocus = (anchorRef) => (e) => {
  R.compose(disableFocus, R.prop('current'))(anchorRef)
  document
    .querySelectorAll('div#mui-rte-toolbar > [class^=MuiButtonBase-root]')
    .forEach(disableFocus)
}

const onAnchorFocus = (editorRef) => (e) => {
  R.compose(
    R.unless(R.isNil, (e) => e.focus()),
    R.prop('current'),
  )(editorRef)
}

const onContainerBlur = (containerRef, anchorRef) => (e) => {
  if (
    containerRef.current &&
    e.relatedTarget &&
    containerRef.current.contains(e.relatedTarget)
  ) {
    // This will prevent this to blur to focus to any element in this container
    // then get trap by focusing anchor element again
    // only when you blur to focus element that is not in this container
    // Then this'll enable anchorRef focusable attribute again.
    // containerRef.current is container holding anchor and editor
    // e.relatedTarget is the target to focus after this blur called. It could be null to indicate focus nothing
    return
  }
  R.compose(enableFocus, R.prop('current'))(anchorRef)
}

// -- RichTextEditor --

const RichTextEditor = (props) => {
  const classes = useStyles()
  const classReadOnly = styleReadOnly()
  const classesInline = useStylesInline()
  const classesBodyBorder = useStylesBorder()
  const windowSize = useWindowSize()

  const theme = useTheme()
  const [{ value, textCount, maxLength }, dispatch] = useReducer(reducer, {
    value: undefined,
    textCount: 0,
  })

  const onChange = props.onChange || R.identity
  const extraMsg = props.extraMsg
  const textStatus = props.textStatus || R.always(null)
  const isDoNotTranslateEnable = R.propOr(true, 'isDoNotTranslateEnable', props)

  const editorRef = useRef(null)

  useEffect(() => {
    dispatch({ type: SET_INITIAL_VALUE, value: props.initialValue })
  }, [])

  useEffect(() => {
    dispatch({ type: SET_MAX_LENGTH, maxLength: props.maxLength })
  }, [props.maxLength])

  const isSkipTranslation = getSkipTranslation(useAppSettings())

  const isShowLabelError = R.gt(textCount, maxLength)

  const isNoRichFormat = props.isNoRichFormat === true
  const linkEnabled = props.linkEnabled === true
  const handleTextChange = (forceUpdate) => (editorState) => {
    if (linkEnabled) {
      editorState = detectLink(editorState)
    }
    const stateContent = editorState.getCurrentContent()

    const [value, isModified] = stateContentToHTML(stateContent, {
      includeDefaultBlockTag: true,
    })
    const textCount = htmlToTextCount(value)
    dispatch({ type: UPDATE_TEXT_COUNT, textCount })
    onChange(textCount > 0 ? value : undefined)
    if (isModified || forceUpdate)
      dispatch({
        type: SET_INITIAL_VALUE,
        value,
      })
  }

  const draftEditorProps = R.mergeRight(
    isNoRichFormat
      ? {
          stripPastedStyles: true,
          handleKeyCommand: () => {},
          handleReturn: () => 'handled',
        }
      : undefined,
    { tabIndex: props.tabIndex },
  )

  const containerRef = useRef(null)
  const anchorRef = useRef(null)

  return (
    <div
      ref={containerRef}
      onFocus={onContainerFocus(anchorRef)}
      onBlur={onContainerBlur(containerRef, anchorRef)}
      className={props.readOnly ? classReadOnly.textReadOnly : ''}
    >
      <div
        tabIndex="0"
        ref={anchorRef}
        onFocus={onAnchorFocus(editorRef, anchorRef)}
      ></div>
      <div
        className={clsx(
          classesBodyBorder.border,
          isShowLabelError
            ? classesBodyBorder.borderRed
            : classesBodyBorder.borderGrey,
        )}
      >
        <MUIRichTextEditor
          ref={editorRef}
          readOnly={props.readOnly}
          draftEditorProps={draftEditorProps}
          label={props.required ? `${props.label} *` : props.label}
          onChange={handleTextChange(false)}
          defaultValue={value}
          classes={props.isControlsInline ? classesInline : classes}
          controls={R.filter(
            R.when(
              R.anyPass([
                R.always(isSkipTranslation),
                R.o(R.not, R.always(isDoNotTranslateEnable)),
              ]),
              R.complement(R.equals(DONOTTRANSLATE_TYPE)),
            ),
            R.isNil(props.controls) ? controls : props.controls,
          )}
          customControls={[
            {
              name: DONOTTRANSLATE_TYPE,
              icon: (
                <DoNotTranslateIcon
                  style={
                    props.customControls
                      ? { marginLeft: windowSize.width - 160 }
                      : null
                  }
                />
              ),
              type: 'inline',
              inlineStyle: {
                borderRadius: 2,
                padding: '2px 0',
                background: R.path(
                  ['palette', 'richTextEditor', 'doNotTranslate', 'background'],
                  theme,
                ),
                '&.MuiIconButton': {
                  root: {
                    padding: '1px',
                  },
                },
              },
            },
            {
              name: 'textCount',
              type: 'callback',
              component: (rest) => (
                <TextCount
                  textStatus={textStatus}
                  maxLength={maxLength}
                  textCount={textCount}
                  isControlsInline={props.isControlsInline}
                  {...rest}
                />
              ),
              inlineStyle: { borderColor: '#E3342F' },
            },
          ]}
        />
      </div>
      {isShowLabelError && (
        <label className="text-red">
          <FormattedMessage
            id="createNotification.dialog.validate.maxLength.message"
            values={{
              maxLength: textCount - maxLength,
            }}
          />
        </label>
      )}

      {extraMsg && (
        <p style={{ fontSize: 10, color: '#a8a8a8', fontFamily: 'Roboto' }}>
          {extraMsg}
        </p>
      )}
    </div>
  )
}

const useStylesTextCount = makeStyles((theme) => ({
  root: {
    display: 'inline-flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    paddingLeft: 4,
  },
  textCountPanel: {
    display: 'flex',
    alignItems: 'center',
  },
  textCount: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const TextCount = (props) => {
  const classes = useStylesTextCount()
  return (
    <div
      className={clsx(
        classes.root,
        props.isControlsInline ? 'flex-grow-0' : 'flex-grow',
      )}
    >
      <div className={classes.textCountPanel}>
        {props.textStatus(props)}
        <Typography variant="caption" className={classes.textCount}>
          {displayCountLimit(props.textCount, props.maxLength)}
        </Typography>
      </div>
    </div>
  )
}

RichTextEditor.propTypes = {
  onChange: PropTypes.func,
  textStatus: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  initialValue: PropTypes.string,
  maxLength: PropTypes.number,
  label: PropTypes.string,
  isNoRichFormat: PropTypes.bool,
}

export default React.memo(RichTextEditor)
